import { NurseLayout } from "@/components/layout/nurse-layout";
import { useGetGuideline, getGetGuidelineQueryKey } from "@workspace/api-client-react";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { useParams, Link } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink, Calendar, AlertTriangle } from "lucide-react";
import { BackButton } from "@/components/ui/back-button";
import { format } from "date-fns";
import { UkHealthDisclaimer } from "@/components/ui/uk-health-disclaimer";

export default function NurseGuidelineDetail() {
  const { id } = useParams();
  const { data: guideline, isLoading, error } = useGetGuideline(Number(id), {
    query: { enabled: !!id, queryKey: getGetGuidelineQueryKey(Number(id)) }
  });

  if (isLoading) return <NurseLayout title="Guideline Detail"><LoadingScreen /></NurseLayout>;
  if (error || !guideline) return <NurseLayout title="Guideline Detail"><div>Guideline not found</div></NurseLayout>;

  return (
    <NurseLayout title={guideline.title}>
      <BackButton href="/nurse/guidelines" className="pl-0 text-slate-500 hover:text-slate-900 -ml-2 mb-6">Back to Guidelines</BackButton>

      <UkHealthDisclaimer variant="nurse" compact />

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 mt-4">
        <div className="flex flex-wrap gap-2 mb-4">
          <Badge variant="outline" className="bg-slate-50">{guideline.organisation}</Badge>
          <Badge variant="secondary" className="bg-teal-50 text-teal-700 hover:bg-teal-50">{guideline.category}</Badge>
          {guideline.tags?.map(tag => (
            <Badge key={tag} variant="outline" className="bg-slate-50 font-normal">{tag}</Badge>
          ))}
        </div>

        <h1 className="text-3xl font-bold text-slate-900 mb-4">{guideline.title}</h1>
        
        {(() => {
          const updatedDate = new Date(guideline.updatedAt);
          const twelveMonthsAgo = new Date();
          twelveMonthsAgo.setFullYear(twelveMonthsAgo.getFullYear() - 1);
          const stale = updatedDate < twelveMonthsAgo;
          return (
            <>
              <div className="flex items-center gap-2 text-sm text-slate-500 mb-4 pb-4 border-b border-slate-100">
                <Calendar className="h-4 w-4" />
                <span>Last verified: {format(updatedDate, "MMMM d, yyyy")}</span>
              </div>
              {stale && (
                <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-6 text-sm text-amber-800">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
                  <p>
                    <strong>Content may be outdated.</strong> This guideline was last verified more than 12 months ago. Please check the original source for the most current version before applying to clinical practice.
                  </p>
                </div>
              )}
            </>
          );
        })()}

        <div className="prose prose-slate max-w-none mb-8">
          <h3 className="text-lg font-semibold text-slate-800 mb-2">Summary</h3>
          <p className="text-slate-700 leading-relaxed bg-slate-50 p-4 rounded-lg">{guideline.summary}</p>
          
          {guideline.fullText && (
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">Key Guidance</h3>
              <div className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                {guideline.fullText}
              </div>
            </div>
          )}
        </div>

        {guideline.url && (
          <div className="pt-6 border-t border-slate-100">
            <a href={guideline.url} target="_blank" rel="norenoopener noreferrer">
              <Button className="bg-slate-900 hover:bg-slate-800 text-white">
                View Original Source <ExternalLink className="h-4 w-4 ml-2" />
              </Button>
            </a>
          </div>
        )}
      </div>
    </NurseLayout>
  );
}
